/**
 * @file 【卡片】AI 助手
 * @author izzieyspu
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Frame, Text, Flex, Divider, Link, HelpTips } from '@tencent/odc';
import AdqIcon from '@tencent/adq-icons';
import _ from 'lodash';
import { MathUtil, hooks } from '@tencent/ptp-fe-common';

import { GlowingEffect } from '@/components/glowing-effect';

import useAIAssistant from '../../hooks/use-ai-assistant';
import { getAccountSummaryData } from '../../api/home';
import Instruction from './Instruction';
import AttentionList from './AttentionList';
import LogoEntry from '../LogoEntry';
import AIReportEntry from '../AIReportEntry';
import { AGENT_ID, InstructionType, AI_ASSISTANT_REPORT_EVENT_NAME } from '../../constants';
import { hasFeatureFlag } from '@/services';

const { useFetch } = hooks;

const SUMMARY_ANALYSIS = 'summary-analysis';

export default () => {
  const { showAIAssistant, aiAssistantModal } = useAIAssistant();

  // 获取账号汇总数据
  const { data: accountSummaryDataResponse } = useFetch(
    getAccountSummaryData,
    {},
    {},
    true,
    undefined,
    (res: any) => res.data || {},
  );
  const summaryDataList = useMemo(() => {
    if (!accountSummaryDataResponse) {
      return [];
    }
    return [
      {
        label: '总消耗',
        value: MathUtil.thousandsSeparated(accountSummaryDataResponse?.today_data?.real_cost_yuan, 2),
        unit: '元',
        sequential_growth: accountSummaryDataResponse?.sequential_growth?.real_cost_yuan_growth,
      },
      {
        label: '今日有消耗客户数',
        value: MathUtil.thousandsSeparated(accountSummaryDataResponse?.today_data?.cost_customer_cnt, 0),
        sequential_growth: accountSummaryDataResponse?.sequential_growth?.cost_customer_cnt_growth,
      },
      {
        label: '今日有消耗账户数',
        value: MathUtil.thousandsSeparated(accountSummaryDataResponse?.today_data?.cost_account_cnt, 0),
        sequential_growth: accountSummaryDataResponse?.sequential_growth?.cost_account_cnt_growth,
      },
      {
        label: '点击率',
        value: MathUtil.getPercent(accountSummaryDataResponse?.today_data?.ctr, 1, 2),
        sequential_growth: accountSummaryDataResponse?.sequential_growth?.ctr_growth,
      },
      {
        label: '浅层转化率',
        value: MathUtil.getPercent(accountSummaryDataResponse?.today_data?.ocpx_conversion_rate, 1, 2),
        sequential_growth: accountSummaryDataResponse?.sequential_growth?.ocpx_conversion_rate_growth,
      },
      {
        label: '深层转化率',
        value: MathUtil.getPercent(accountSummaryDataResponse?.today_data?.second_conversion_rate, 1, 2),
        sequential_growth: accountSummaryDataResponse?.sequential_growth?.second_conversion_rate_growth,
      },
      {
        label: '转化成本',
        value: MathUtil.thousandsSeparated(accountSummaryDataResponse?.today_data?.conversions_cost, 2),
        unit: '元',
        sequential_growth: accountSummaryDataResponse?.sequential_growth?.conversions_cost_growth,
      },
    ];
  }, [accountSummaryDataResponse]);

  // 唤起AI助手
  const handleAIAssistantShow = useCallback((props: { type?: InstructionType | typeof SUMMARY_ANALYSIS, content?: string }) => {
    const { type, content } = props;
    const options = (() => {
      if (type === InstructionType.AGENT) {
        return {
          defaultFeatureType: content,
        };
      }
      if (type === InstructionType.CONTENT) {
        return {
          defaultPrompt: content,
        };
      }
      if (type === SUMMARY_ANALYSIS) {
        return {
          defaultPrompt: content,
          targetAgentId: AGENT_ID.SUMMARY_ANALYSIS,
        };
      }
      return {};
    })();
    showAIAssistant(options);
  }, [showAIAssistant]);

  // 管理请求中断
  const streamAbortRef = useRef<AbortController | null>(null);

  // 卸载时中断流
  useEffect(
    () => () => {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
      }
    },
    [],
  );

  // 使用指南
  const handleUseGuide = useCallback(() => {
    window.open('https://docs.qq.com/doc/DSU1HbWxqbWxydVFG', '_blank');
  }, []);

  return (
    <Frame
      radius={12}
      className="tw-px-[36px] tw-py-[24px] tw-relative tw-w-full"
      style={{
        background:
          'linear-gradient(279deg, rgba(255, 255, 255, 0.80) 5.59%, rgba(255, 255, 255, 0.80) 30.61%), linear-gradient(167deg, rgba(215, 196, 255, 0.80) -6.43%, rgba(255, 255, 255, 0.80) 17.74%), linear-gradient(0deg, #FFF 0%, #FFF 100%), var(---White00, #FFF)',
      }}
    >
      <GlowingEffect
        spread={40}
        glow={true}
        disabled={false}
        proximity={64}
        inactiveZone={0.01}
      />
      <Flex column gap={24}>
        <Flex column justifyContent="between" gap={16}>
          <Flex justifyContent="between">
            <Flex itemsCenter>
              <LogoEntry position="home-card" />
              <Divider type="vertical" gap={12} />
              <Text type="primary" size="large" label="Hi，我是你的专属AI助理，已为你完成全景扫描" block as="strong" />
            </Flex>
            <Flex gap={16}>
              {hasFeatureFlag('is_enable_ai_op_dashboard') ? (
                <AIReportEntry reportActionName={`${AI_ASSISTANT_REPORT_EVENT_NAME.AI_REPORT_ENTRY}__home`} />
              ) : (
                <Text
                  className='tw-flex tw-items-center tw-cursor-pointer'
                  size='small'
                  type='secondary'
                  icon={<AdqIcon name='info-circle-filled' style={{ color: 'rgba(58, 69, 84, 0.36)' }} />}
                  onClick={handleUseGuide}
                  data-report={AI_ASSISTANT_REPORT_EVENT_NAME.GUIDE_LINK}
                >使用指南</Text>
              )}
            </Flex>
          </Flex>
          {summaryDataList?.length > 0 ? (
            <Frame className="tw-inline tw-align-middle tw-leading-[20px]">
              <Frame className="tw-inline tw-align-middle tw-leading-[20px]">
                <Text type="primary" label="最新分时数据" suffix={<HelpTips tips="分时数据范围是你的权限范围内的所有账户，对比口径为今日与昨日同一时刻的数据对比。数据每1h更新，可能与报表模块存在一定差异。" />} inlineBlock className="tw-align-middle tw-leading-[20px]" />
                <Text type="primary" label="：今日账户" inlineBlock className="tw-align-middle tw-leading-[20px]" />
                {_.map(summaryDataList, ({ label, value, unit, sequential_growth }, idx) => (
                  <Frame key={label} className="tw-inline tw-align-middle tw-leading-[20px]">
                    <Text type="primary" label={label} inlineBlock className="tw-leading-[20px]" />
                    <Text
                      type="primary"
                      label={value}
                      as="strong"
                      inlineBlock
                      className="tw-px-[4px] tw-leading-[20px]"
                    />
                    {unit && <Text type="primary" label={unit} inlineBlock className="tw-leading-[20px]" />}
                    <Flex
                      alignItems="center"
                      inline
                      className="tw-align-middle tw-leading-[20px]"
                      style={{
                        color: sequential_growth > 0 ? 'var(---Green-07, #00AA37)' : 'var(---Red-07, #E80000)',
                      }}
                    >
                      <AdqIcon
                        name={sequential_growth > 0 ? 'rise-filled' : 'fall-filled'}
                        className="-mr-4 tw-align-middle"
                      />
                      <Text type="primary" className="tw-leading-[20px]">{MathUtil.getPercent(sequential_growth, 1, 2)}</Text>
                    </Flex>
                    {idx < summaryDataList?.length - 1 && (
                      <Text type="primary" label="，" className="tw-leading-[20px]" />
                    )}
                  </Frame>
                ))}
              </Frame>
              { }
              <Link
                onClick={() => handleAIAssistantShow({ type: SUMMARY_ANALYSIS, content: '帮我详细分析首页汇总数据' })}
                className="tw-ml-[4px] tw-align-middle"
                data-report={AI_ASSISTANT_REPORT_EVENT_NAME.SUMMARY_ANALYSIS}
              >
                详细分析
              </Link>
            </Frame>
          ) : <Text type="primary" label="数据分析中..." inlineBlock className="tw-leading-[20px]" />}
          <AttentionList />
        </Flex>
        <Instruction onClick={({ type, text }) => handleAIAssistantShow({ type, content: text })} />
      </Flex>
      {aiAssistantModal}
    </Frame>
  );
};
